import React from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import { getFormValues } from "redux-form";
import classNames from "classnames";

import CustomControlWrapper from "./CustomControlWrapper";
import RawInput from "./RawInput";
import VariableNameInput from "./VariableNameInput";
import DynamicToggle from "./DynamicToggle";
import { useTooltipRebuild } from "hooks/useTooltipRebuild";

import "./DynamicInput.css";

function reduxWrapper(Control){
	return function RDX({ input, meta, ...props }){
		return <Control {...input} {...props} meta={meta} />;
	};
}

const CustomInput = reduxWrapper(CustomControlWrapper(RawInput));

export default function DynamicInput(props){
	const {
		defaultDynVarName,
		dynamicToggleDisabled,
		variableNameInputDisabled,
		variableNameInputTip,
		...forwardedProps // We just need to separate these to forward to the Input without DOM warnings
	} = props;
	const { input, meta, disabled } = forwardedProps;
	const { onChange, onBlur } = input || {};
	const { name } = input || {};
	const [adType] = name.split(".");

	const creativeValues = useSelector(state => getFormValues(meta.form)(state)?.[adType] || {}); /*
		This makes some assumptions about the structure of our forms where we use this, which
		isn't ideal, but it's dramatically simpler than passing the adType in as a prop
	*/
	const isCreativeDynamic = creativeValues?.isDynamic;

	useTooltipRebuild(isCreativeDynamic);

	const initialDynVarName = (typeof(defaultDynVarName) === "function") ? defaultDynVarName(input, creativeValues) : defaultDynVarName;

	const value = input.value || {
		value: "",
		isDynamic: false,
		dynVarName: initialDynVarName,
	};

	if(!value.dynVarName){
		// We may get the value set from Shared Values without the default variable name
		value.dynVarName = initialDynVarName;
	}

	const { isDynamic } = value || {};

	const className = classNames({
		"dynamic-input": true,
		"is-dynamic": isDynamic,
		disabled,
		[props.className]: !!props.className,
	});

	function onInputChange(newValue){
		onChange?.({
			...value,
			value: newValue,
		});
	}

	function onInputBlur(event){
		const newValue = event?.target?.value;
		onBlur?.({
			...value,
			value: newValue,
		});
	}

	function onToggleChange(){
		if(!disabled && !dynamicToggleDisabled){
			onChange({
				...value,
				isDynamic: !value.isDynamic,
			});
		}
	}

	function onVariableNameChange(newName){
		onChange({
			...value,
			dynVarName: newName,
		});
	}

	const inputProps = {
		...forwardedProps,
		input: {
			...props.input,
			value: value?.value ?? "",
			onBlur: onInputBlur,
			onChange: onInputChange,
		},
		className,
	};

	if(isCreativeDynamic){
		const tip = isDynamic ? "Disable dynamic field" : "Enable dynamic field";
		const detail = !isDynamic ? null : (<VariableNameInput
			disabled={disabled || variableNameInputDisabled}
			tip={variableNameInputTip}
			value={value.dynVarName}
			onChange={onVariableNameChange}
		/>);

		inputProps.pre = (
			<DynamicToggle
				onClick={onToggleChange}
				tip={tip}
				isDynamic={isDynamic}
				isDisabled={disabled || dynamicToggleDisabled}
			/>
		);
		inputProps.detail = detail;
	}

	return (
		<CustomInput {...inputProps} />
	);
}

DynamicInput.displayName = "DynamicInput";

DynamicInput.defaultProps = {
	defaultDynVarName: "",
	dynamicToggleDisabled: false,
	variableNameInputDisabled: false,
};

DynamicInput.propTypes = {
	defaultDynVarName: PropTypes.oneOfType([PropTypes.string, PropTypes.func]), /*
		If this is provided as a function, it will receive the `input` prop, and
		the creative values as arguments, and it should return a string.

		This can be used to generate a default variable name based on the field name, etc.
		This is useful if you want to prefix a name with a card index, for example.
	*/
	input: PropTypes.object,
	meta: PropTypes.object,
	disabled: PropTypes.bool,
};
